import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { NotificationsDropdown } from './NotificationsDropdown';
// Icons here are the ones referenced by the nav-item arrays below + LogOut (the
// header logout button). Menu/X live inside ResponsiveNav now. Calculator (Accounting)
// + Layers (F&I Menus) were removed when those moved into Settings (no longer nav items).
import { Car, ClipboardList, Users, LogOut, UserPlus, BarChart3, FileText, Gavel , Megaphone, FileSearch, CreditCard, Scale, Landmark, Wrench, Cpu } from 'lucide-react';
import { Logo } from './Logo';
import { BUILD_ID } from '../version';
import api from '../utils/api';
import { seedEntitlements, refreshEntitlements } from '../utils/entitlementsCache';
import { ResponsiveNav } from './ResponsiveNav';
import { useCustomerPresentation } from '../contexts/CustomerPresentationContext';

export const Layout = ({ children }) => {
  const { presenting } = useCustomerPresentation();
  const { user, logout, exitImpersonation, isDealershipUser, isVendorUser } = useAuth();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [entitlements, setEntitlements] = useState(() => seedEntitlements() || {});

  const fetchUnreadCount = async () => {
    try {
      const response = await api.get('/notifications/unread-count');
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  useEffect(() => {
    fetchUnreadCount();
    // Hidden tabs skip the tick (perf P27) — the bell refreshes on focus return.
    const interval = setInterval(() => { if (!document.hidden) fetchUnreadCount(); }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Which gated modules (CRM/DMS) this dealership is entitled to — drives nav
  // visibility. NOT the security boundary (that's the server-side route gate);
  // a non-entitled dealer that forced /crm still gets 403 from the API.
  useEffect(() => {
    if (isDealershipUser()) {
      // Session-cached: paints instantly from the seed; this quiet refresh picks
      // up entitlement changes without re-asking on every navigation.
      refreshEntitlements(setEntitlements);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Per-user module visibility (2026-06-10): an Owner can restrict a user to a
  // subset of modules (a recon-only mechanic). `user.module_access` is null for
  // everyone unrestricted (sees all) — so this is purely subtractive and never
  // hides anything from existing users. Owners/SuperAdmins always see all. This
  // is the UX gate; the server route gates are the real enforcement.
  const ownerLike = ['Owner', 'SuperAdmin', 'SupportAdmin'].includes(user?.role);
  const moduleAccess = user?.module_access; // null/undefined = unrestricted
  const canSeeModule = (mod) =>
    !mod || ownerLike || !moduleAccess || moduleAccess.includes(mod);

  // Dashboard is intentionally NOT a nav item — it's reached by clicking the
  // logo (top-left), which frees header room. CRM leads the nav when entitled.
  // Each item carries its module key (`mod`); items with no `mod` (Settings)
  // are always shown. CRM/DMS still require the dealership entitlement first.
  const dealerNavItems = [
    // CRM is one entry now: it opens the hub on My Day, and Calendar lives as a
    // tab inside it (was a separate top-level item). `match` keeps the nav item
    // highlighted across every /crm/* tab + lead detail (CRM_WORK_PLAN.md).
    ...(entitlements.crm ? [{ path: '/crm/my-day', label: 'CRM', icon: UserPlus, mod: 'crm', match: '/crm' }] : []),
    ...(entitlements.dms ? [{ path: '/dms/deals', label: 'Deals', icon: FileText, mod: 'dms' }] : []),
    // Finance desk (Deal Router / Matcher / Max Price) — Owner/Manager-only
    // server-side; the page shows an access note if a Staff user navigates here.
    ...(entitlements.dms ? [{ path: '/dms/finance', label: 'Finance', icon: Landmark, mod: 'dms' }] : []),
    // F&I menu builder is no longer a top-nav item — it lives as a section inside
    // Settings (DMS-entitled, header declutter). Per-deal menu presentation stays
    // on the deal desk; the public /fi/:token customer view is unchanged.
    // Forms setup and deal accounting live in Settings. Printing and saved
    // paperwork remain on each deal; their routes and server gates are unchanged.
    { path: '/evaluator', label: 'Evaluator', icon: Gavel, mod: 'evaluation' },
    { path: '/vehicles', label: 'Vehicles', icon: Car, mod: 'vehicles' },
    // Recon hub (formerly "Tasks"). Parts Inbox + Estimates now live as tabs
    // INSIDE the Recon hub, not top-level nav items.
    { path: '/recon', label: 'Recon', icon: ClipboardList, mod: 'recon' },
    // Parts catalog operator page (entitlement-gated module, like CRM/DMS).
    // Recon parts requests stay in the Parts Inbox tab inside the Recon hub.
    ...(entitlements.parts ? [{ path: '/parts', label: 'Parts', icon: Wrench, mod: 'parts' }] : []),
    // Auction arbitration queue — Owner/Manager only (they work arbitrations).
    ...(['Owner', 'Manager'].includes(user?.role)
      ? [{ path: '/arbitrations', label: 'Arbitrations', icon: Scale, mod: 'recon' }] : []),
    // Invoices moved OUT of the top nav into the Vendors area (per-vendor +
    // an all-vendors approval queue reachable from /vendors). Declutters the
    // header. The /invoices route still exists; it's just not a nav item.
    { path: '/vendors', label: 'Vendors', icon: Users, mod: 'vendors' },
    // Reports is PII-free, ungated infra. Gated module reports (CRM/DMS) only
    // surface inside /reports when entitled.
    { path: '/reports', label: 'Reports', icon: BarChart3, mod: 'reports' },
    // Marketing — AI listing copy + publish to the public website. PII-free,
    // ungated infra; per-user visibility only (mod: 'marketing').
    { path: '/marketing', label: 'Marketing', icon: Megaphone, mod: 'marketing' },
    // Accounting (read-only per-VIN cost rollups) is no longer a top-nav item — it
    // lives as a section inside Settings (per-user 'accounting' visibility, header
    // declutter). PII-free, tenant-scoped API.
    // Dealer Billing is no longer a top-nav item — it lives as an Owner-only
    // section inside Settings (header declutter). SuperAdmin platform billing
    // stays in adminNavItems below.
    // Settings (own profile) is always available — no module key.
    { path: '/settings', label: 'Settings', icon: Users },
  ].filter((item) => canSeeModule(item.mod));

  const vendorNavItems = [
    { path: '/vendor/tasks', label: 'My Tasks', icon: ClipboardList },
    { path: '/settings', label: 'Settings', icon: Users },
  ];
  
  const adminNavItems = [
    { path: '/admin', label: 'Admin', icon: Users },
    { path: '/admin/parser-health', label: 'Parser Health', icon: FileSearch },
    { path: '/admin/billing', label: 'Billing', icon: CreditCard },
    { path: '/admin/ai-models', label: 'AI Models', icon: Cpu },
  ];

  const navItems = user?.role === 'SuperAdmin' ? adminNavItems : (isDealershipUser() ? dealerNavItems : vendorNavItems);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-muted">
      {/* Support-session banner: always visible while impersonating, on every
          page, above the app chrome — there must be no way to forget whose
          account is being operated. See docs/specs/IMPERSONATION.md. */}
      {user?.impersonated_by && (
        <div
          data-testid="impersonation-banner"
          className="sticky top-0 z-[60] w-full bg-amber-500 text-amber-950 text-sm font-medium px-4 py-2 flex items-center justify-center gap-2 flex-wrap"
        >
          <span>
            Support session — viewing as <b>{user.name}</b> ({user.email}). Their permissions apply; actions are real.
          </span>
          <button
            type="button"
            onClick={exitImpersonation}
            data-testid="impersonation-exit"
            className="underline underline-offset-2 font-bold"
          >
            Exit
          </button>
        </div>
      )}
      {!presenting && <header className="sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <div className="flex h-14 md:h-16 items-center px-4 md:px-6">
          {/* Logo is ALWAYS visible (leftmost) — both layouts of ResponsiveNav sit
              beside it: a horizontal row on wide screens, a hamburger on narrow. */}
          <Link
            to="/dashboard"
            data-testid="logo-home-link"
            aria-label="Go to dashboard"
            className="rounded-lg transition-opacity hover:opacity-80"
          >
            <Logo />
          </Link>

          <ResponsiveNav items={navItems} ariaLabel="Primary" />

          <div className="ml-auto flex items-center gap-2">
            <NotificationsDropdown unreadCount={unreadCount} onUpdate={fetchUnreadCount} />

            <div className="flex items-center gap-2 pl-2 border-l border-border">
              <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center text-sm font-medium">
                {user?.name?.charAt(0)}
              </div>
              <div className="hidden md:block">
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs text-muted-foreground">{user?.role}</p>
              </div>
              <button
                data-testid="logout-button"
                onClick={handleLogout}
                className="p-2 hover:bg-accent rounded-lg transition-colors"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

      </header>}

      <main className="pb-8">{children}</main>

      {!presenting && <footer className="hidden md:block border-t border-border mt-12 py-4 px-4">
        <p className="max-w-7xl mx-auto text-[11px] text-muted-foreground text-center">
          DealerFlow is an Amazon Associate. We may earn from qualifying purchases when you order through Amazon links shown in the app.
        </p>
        {/* Deploy verification: "is the new bundle live?" is answered by eye
            here or by fetching /version.json — no more grepping bundles. */}
        <p data-testid="build-id" className="max-w-7xl mx-auto text-[11px] text-muted-foreground/70 text-center mt-1">
          Build {BUILD_ID.slice(0, 12)}
        </p>
      </footer>}

    </div>
  );
};
